import {Component, DestroyRef, Inject, inject, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {AbstractControl, FormControl, ReactiveFormsModule, ValidationErrors, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatIconModule} from '@angular/material/icon';
import {MatSelectModule} from '@angular/material/select';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatButtonModule} from '@angular/material/button';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

import {DataService} from '../../services/data.service';
import {UserPickerComponent} from '../../shared/components/user-picker.component';



export function intParsableValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value !== null && value !== undefined && Number.isNaN(parseInt(value, 10))) {
        return {numberParsable: 'Not parsable'};
    }
    return null;
}

interface PaymentMethod {
    methodValue: string;
    methodCode: string;
}

@Component({
    selector: 'app-open-project-dialog',
    templateUrl: './open-project-dialog.component.html',
    styleUrl: './open-project-dialog.component.scss',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatSelectModule,
        MatDatepickerModule,
        MatButtonModule,
        UserPickerComponent
    ]
})
export class OpenProjectDialogComponent implements OnInit {
    private destroyRef = inject(DestroyRef);

    public projectNr = new FormControl<string>({value: '', disabled: false}, {
        nonNullable: true,
        validators: [Validators.required, intParsableValidator]
    });
    public finalOffer = new FormControl<number>(0, {nonNullable: true, validators: [Validators.required]});
    public projectManager = new FormControl<any>(null, {validators: [Validators.required]});
    public deliveryDate = new FormControl<Date>(new Date(), {nonNullable: true, validators: [Validators.required]});
    public paymentMethod = new FormControl<string>('', {nonNullable: true, validators: [Validators.required]});

    public minDeliveryDate = new Date();
    public paymentMethodOptions: PaymentMethod[] = [];
    public isExport = false;

    constructor(
        public dialogRef: MatDialogRef<OpenProjectDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private dataService: DataService
    ) {
    }

    get isValid(): boolean {
        return (
            (this.projectNr.disabled || this.projectNr.valid) &&
            this.finalOffer.valid &&
            this.projectManager.valid &&
            this.paymentMethod.valid &&
            !!this.deliveryDate.value
        );
    }

    ngOnInit(): void {
        this.isExport = !!this.data?.budget?.export;

        if (this.isExport) {
            this.projectNr.setValue(`${this.data.projectNrITA}NY`);
            this.projectNr.disable();
        } else {
            this.projectNr.enable();
            this.dataService.getNextProjectNr()
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((projectNr) => this.projectNr.setValue(projectNr));
        }

        if (this.data?.oData?.offerAmount) {
            this.finalOffer.setValue(Math.round(this.data.oData.offerAmount * 100) / 100);
        }

        if (this.data?.user) {
            this.projectManager.setValue(this.data.user);
        }

        this.dataService.getPaymentMethods()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((paymentMethods) => {
                this.paymentMethodOptions = paymentMethods;
            });
    }

    getErrorMessage(): string {
        if (this.projectNr.hasError('required')) {
            return 'You must enter a value';
        }
        if (this.projectNr.hasError('numberParsable')) {
            return 'Project number must start with an integer';
        }
        return '';
    }

    closeDialog(): void {
        if (this.isValid) {
            const selected = this.paymentMethodOptions.find(
                (pm) => pm.methodValue === this.paymentMethod.value
            );

            this.dialogRef.close({
                projectNr: this.projectNr.getRawValue(),
                finalOffer: this.finalOffer.value,
                projectManager: this.projectManager.value,
                paymentMethod: this.paymentMethod.value,
                paymentMethodCode: selected?.methodCode ?? '',
                deliveryDate: this.deliveryDate.value
            });
        }
    }

    onNoClick(): void {
        this.dialogRef.close();
    }
}